import createClient from 'openapi-fetch';
import type { components, paths } from '@familychores/api-contract/schema';

export type Schemas = components['schemas'];

/** RFC 9457 problem details as returned by the API (docs/api-design.md §6). */
export interface Problem {
  type?: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  requestId?: string;
  errors?: { field: string; message: string }[];
  resource?: unknown;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }

  get code(): string {
    return this.problem.code;
  }

  /** The message to show a person: specific detail when available, otherwise the title. */
  get userMessage(): string {
    return this.problem.detail ?? this.problem.title;
  }
}

// ── Token handling (docs/security.md §1.1) ─────────────────────────────────────────────────────────
// The access token lives only in memory. The refresh token is an HttpOnly cookie the page can't read.

let accessToken: string | null = null;
const authLostListeners = new Set<() => void>();

export const tokens = {
  get: () => accessToken,
  set: (t: string) => {
    accessToken = t;
  },
  clear: () => {
    accessToken = null;
  },
  onAuthLost: (fn: () => void) => {
    authLostListeners.add(fn);
    return () => authLostListeners.delete(fn);
  },
};

let refreshing: Promise<boolean> | null = null;

/**
 * Single-flight refresh. Within a tab, concurrent 401s share one refresh; across tabs, the Web Locks API serializes
 * rotations (the server also tolerates a replay within a short grace window).
 */
export function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    const run = async () => {
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-csrf-protection': '1' },
        body: '{}',
      });
      if (!res.ok) {
        tokens.clear();
        return false;
      }
      const body = (await res.json()) as { accessToken: string };
      tokens.set(body.accessToken);
      return true;
    };
    return typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request('familychores-refresh', run) : run();
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function problemCode(res: Response): Promise<string | undefined> {
  try {
    return ((await res.clone().json()) as Problem).code;
  } catch {
    return undefined;
  }
}

/** fetch with bearer auth; on TOKEN_EXPIRED refreshes once and retries. */
export async function authFetch(input: Request): Promise<Response> {
  const send = (req: Request) => {
    const r = req.clone();
    const token = tokens.get();
    if (token) r.headers.set('Authorization', `Bearer ${token}`);
    return fetch(r);
  };
  const original = input.clone();
  let res = await send(input);
  if (res.status === 401) {
    const code = await problemCode(res);
    if (code === 'TOKEN_EXPIRED' || (code === 'UNAUTHENTICATED' && tokens.get() === null)) {
      if (await refreshAccessToken()) res = await send(original);
    }
    if (res.status === 401) authLostListeners.forEach((fn) => fn());
  }
  return res;
}

// Same origin as the page (production serves web + /api behind one proxy; dev proxies /api through Vite).
export const api = createClient<paths>({ baseUrl: typeof window === 'undefined' ? '' : window.location.origin, fetch: authFetch, credentials: 'same-origin' });

/** Unwraps an openapi-fetch result: returns data or throws ApiError with the server's problem details. */
export async function call<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || !response.ok) {
    const problem = (error ?? {}) as Partial<Problem>;
    throw new ApiError(response.status, {
      status: response.status,
      code: problem.code ?? `HTTP_${response.status}`,
      title: problem.title ?? 'Something went wrong. Please try again.',
      ...problem,
    } as Problem);
  }
  return data as T;
}
